import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import useSongStore from '../../spotipitan/store/SongState';
import SquareImage from '../../../shared/square-image/square-image';
import useUserStore from '../../../../store/UserState';
import { isAppleDevice } from '../../../../utils/device-utils';
import YoutubeIframe, { YoutubeIframeRef } from '../../../shared/youtube-iframe/youtube-iframe';
import LocalStorage from '../../../../utils/local-storage';

import './desktop-right-sidebar.scss';
import classNames from 'classnames';
import { useNavigateToArtist, useResolvedTrack, useTrackCanvas } from '../../../../hooks';
import { auth } from '../../../../firebase';

export const ENABLE_VIDEO_IN_SIDEBAR = false;

const defaultIcon = "https://torresowo.myftp.org:8082/files/cd812ccb405099877fe1964a4ba6cf2d";

const DesktopRightSidebar = () => {
    const { state } = useSongStore(state => state);
    const { currentSong } = state;
    const [favourite, setFavorite] = useState(currentSong?.is_favorite);
    const [showVideo, setShowVideo] = useState(false);
    const { user, offlineMode } = useUserStore(state => state.state);
    const [sidebarMaximized, setSidebarMaximized] = useState(false);
    const { canvasInfo, canvasUrl } = useTrackCanvas(currentSong);
    const { title: songTitle, artistText: songArtist, resolvedTrack } = useResolvedTrack(currentSong);
    const { navigateToArtist } = useNavigateToArtist();

    const songHasVideo = useMemo(() => {
        return !state.isBotMode && !!currentSong && isNaN(currentSong.id as any);
    }, [currentSong, state.isBotMode]);

    const youtubePlayerRef = useRef<YoutubeIframeRef>(null);
    const canvasVideoRef = useRef<HTMLVideoElement>(null);
    const lastSyncTime = useRef<number>(0);

    useEffect(() => {
        const audioEl = document.getElementById('audio-player') as HTMLAudioElement;
        if (!audioEl) return;

        const handlePause = () => {
            if (canvasVideoRef.current) canvasVideoRef.current.pause();
        };
        const handlePlay = () => {
            if (canvasVideoRef.current) canvasVideoRef.current.play().catch(() => { });
        };

        audioEl.addEventListener('pause', handlePause);
        audioEl.addEventListener('play', handlePlay);

        if (audioEl.paused && canvasVideoRef.current) {
            canvasVideoRef.current.pause();
        }

        return () => {
            audioEl.removeEventListener('pause', handlePause);
            audioEl.removeEventListener('play', handlePlay);
        };
    }, [canvasUrl]);

    useEffect(() => {
        setShowVideo(LocalStorage.getItem("showVideo") === "true");

        const handleShowVideoChanged = (e: Event) => {
            const customEvent = e as CustomEvent;
            setShowVideo(customEvent.detail);
        };
        window.addEventListener("showVideoChanged", handleShowVideoChanged);
        return () => {
            window.removeEventListener("showVideoChanged", handleShowVideoChanged);
        };
    }, []);

    const handleSetShowVideo = (value: boolean) => {
        setShowVideo(value);
        LocalStorage.setItem("showVideo", value ? "true" : "false");
        window.dispatchEvent(new CustomEvent("showVideoChanged", { detail: value }));
    };

    useEffect(() => {
        if (!currentSong) return;
        setFavorite(currentSong?.is_favorite);
    }, [currentSong]);

    const hasCanvas = useMemo(() => songHasVideo || canvasUrl, [songHasVideo, canvasUrl]);

    useLayoutEffect(() => {
        if ((!showVideo && !hasCanvas) || !songHasVideo || !currentSong) return;

        const interval = setInterval(() => {
            const audioEl = document.getElementById('audio-player') as HTMLAudioElement;
            if (!youtubePlayerRef.current) return;
            const player = youtubePlayerRef.current.getPlayer();
            if (!audioEl || !player) return;

            const now = Date.now();
            if (now - lastSyncTime.current < 200) return;
            lastSyncTime.current = now;

            const audioTime = audioEl.currentTime;
            try {
                const playerState = player.getPlayerState();

                // 5: cued/stopped, 1: playing, 2: paused
                if (playerState === 5 && !audioEl.paused) {
                    youtubePlayerRef.current.seekTo(audioTime);
                    youtubePlayerRef.current.play();
                    return;
                }

                if (playerState === 1 || playerState === 2) {
                    if (audioEl.paused && playerState === 1) {
                        youtubePlayerRef.current.pause();
                    } else if (!audioEl.paused && playerState === 2) {
                        youtubePlayerRef.current.play();
                    }

                    const videoTime = player.getCurrentTime();
                    if (typeof videoTime === 'number') {
                        if (Math.abs(videoTime - audioTime) > 0.3) {
                            youtubePlayerRef.current.seekTo(audioTime);
                        }
                    }
                }
            } catch (error) {
                console.error("Error al sincronizar video en sidebar:", error);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [showVideo, songHasVideo, currentSong, hasCanvas]);

    const handleClickButton = (e: React.MouseEvent<HTMLElement>, id: string) => {
        const original = document.getElementById(id);
        if (original) {
            // Create a new event that bubbles and is cancelable
            const event = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: e.clientX,
                clientY: e.clientY,
                screenX: e.screenX,
                screenY: e.screenY,
                metaKey: e.metaKey,
                ctrlKey: e.ctrlKey,
                shiftKey: e.shiftKey,
                altKey: e.altKey,
            });

            original.dispatchEvent(event);
        } else {
            console.error('btn not found: ' + id)
        }
    };

    const handleAddFavourite = () => {
        const favBtn = document.getElementById('current-song-add-favs-btn');
        if (!favBtn) {
            return;
        }

        setFavorite(prev => !prev);

        favBtn.click();
    }

    const handleToggleMaximize = () => {
        const container = document.querySelector('.spotipitan-right-sidebar');

        if (!container) { return; }

        const isMaximized = !sidebarMaximized;
        setSidebarMaximized(isMaximized);
        container.classList.toggle('spotipitan-right-sidebar--maximized');
    };

    return (
        <div className='desktop-right-sidebar'>
            {hasCanvas && (
                <div className='desktop-right-sidebar__canvas'>
                    <div className="desktop-right-sidebar__canvas__shadow-top" />
                    <div className="desktop-right-sidebar__canvas__shadow-bottom" />
                    {canvasUrl && <video
                        ref={canvasVideoRef}
                        key={canvasUrl}
                        src={canvasUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="desktop-right-sidebar__canvas__video"
                    />}
                    {songHasVideo &&
                        <YoutubeIframe
                            ref={youtubePlayerRef}
                            style={{
                                pointerEvents: "none",
                                height: "100%",
                                aspectRatio: 16 / 9,
                                width: "auto",
                            }}
                            className="song-player-poster-video"
                            videoId={currentSong!.id}
                        />}
                </div>
            )}

            <div className={classNames("desktop-right-sidebar__content", {
                'desktop-right-sidebar__has-canvas': hasCanvas
            })}>
                <div className={classNames("desktop-right-sidebar__poster", {
                    'desktop-right-sidebar__poster--play': showVideo && songHasVideo
                })}>
                    <div className='song-player-poster-info'>
                        <div className='song-player-poster-info__name'>
                            {songTitle || currentSong?.name}
                        </div>
                        <div className='song-player-poster-info__artist text-muted'>
                            {resolvedTrack?.artists && resolvedTrack.artists.length > 0 ? (
                                resolvedTrack.artists.map((art, idx) => {
                                    const disabledBtn = !art.id || art.id?.toString() == "999";
                                    return <React.Fragment key={art.id || art.name || idx}>
                                        <span
                                            className={classNames({
                                                "desktop-right-sidebar__artist-link": !disabledBtn
                                            })}
                                            onClick={(e) => {
                                                if (disabledBtn) {
                                                    return;
                                                }
                                                e.stopPropagation();
                                                navigateToArtist(art);
                                            }}
                                            title={art.id ? `Ver perfil de ${art.name}` : undefined}
                                        >
                                            {art.name}
                                        </span>
                                        {idx < (resolvedTrack.artists?.length || 0) - 1 && ", "}
                                    </React.Fragment>
                                })
                            ) : (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (currentSong?.user?.id) {
                                            navigateToArtist(currentSong.user.id);
                                        } else {
                                            navigateToArtist(undefined, songArtist || currentSong?.user?.username);
                                        }
                                    }}
                                    title={songArtist || currentSong?.user?.username ? `Ver perfil de ${songArtist || currentSong?.user?.username}` : undefined}
                                >
                                    {songArtist || currentSong?.user?.username}
                                </span>
                            )}
                        </div>
                    </div>

                    <div
                        className="song-player-poster-clone"
                        onContextMenu={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClickButton(e, 'current-song-options-btn');
                        }}
                    >
                        <SquareImage
                            song={currentSong ? { ...currentSong, poster_url: resolvedTrack?.coverUrl ?? currentSong.poster_url } : undefined}
                            big={true}
                            src={defaultIcon}
                            alt={currentSong?.name ?? "Selecciona una canción"}
                            className="song-player-poster"
                        />
                    </div>
                </div>
                {ENABLE_VIDEO_IN_SIDEBAR && showVideo && songHasVideo && (
                    <div className='desktop-right-sidebar__video'>
                        <YoutubeIframe
                            ref={youtubePlayerRef}
                            style={{
                                pointerEvents: "none",
                                width: "100%",
                                height: "100%"
                            }}
                            className="song-player-poster-video"
                            videoId={currentSong!.id}
                        />
                    </div>
                )}
                <div className='song-player-poster-options'>
                    <div className='song-player-poster-options__left'>
                        <button onClick={(e) => handleClickButton(e, 'current-song-options-btn')}>
                            <i className="fa-solid fa-bars"></i>
                        </button>
                        {auth.currentUser && <button className={classNames('song-player-poster-options__fav-btn', {
                            'is-favorite': favourite
                        })} onClick={handleAddFavourite}>
                            <i className="fa-solid fa-heart-broken"></i>
                            <i className="fa-solid fa-heart"></i>
                            <i className="fa-regular fa-heart"></i>
                        </button>}
                        <button onClick={(e) => handleClickButton(e, 'current-song-copy-link')}>
                            <i className="fa-solid fa-share-nodes"></i>
                        </button>
                    </div>
                    <div className='song-player-poster-options__right'>
                        {ENABLE_VIDEO_IN_SIDEBAR && <button onClick={handleToggleMaximize}>
                            <i className={classNames("fa-solid", {
                                "fa-up-right-and-down-left-from-center": !sidebarMaximized,
                                "fa-down-left-and-up-right-to-center": sidebarMaximized
                            })}></i>
                        </button>}
                        {ENABLE_VIDEO_IN_SIDEBAR && user?.uid && !offlineMode && songHasVideo && (
                            <button onClick={() => handleSetShowVideo(!showVideo)}>
                                <i className={classNames("fa-solid fa-video", {
                                    "text-warning": showVideo
                                })}></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesktopRightSidebar;